import { API_PREFIX, ApiPath, apiErrorSchema, type ApiError } from '@app/shared';
import { z, type ZodTypeAny } from 'zod';

export type ApiResult<T> =
  { success: true; data: T; message: string } | { success: false; error: ApiError };

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Di produksi, frontend dipisah dari Worker API → pakai URL absolute + prefix /api/v1.
// Dev (Vite proxy) & build lokal tanpa env → relatif /api/v1.
const API_BASE = (import.meta.env.VITE_API_URL ?? '') + API_PREFIX;

type QueryValue = string | number | boolean | undefined | null;
type QueryParams = Record<string, QueryValue | QueryValue[]>;

function shouldRefresh(method: string, path: string): boolean {
  if (path === ApiPath.AUTH.LOGIN || path === ApiPath.AUTH.REFRESH) return false;
  // This one read is intentionally public; settings mutations remain protected.
  if (method === 'GET' && path === ApiPath.SETTINGS.INSTITUTION) return false;
  return true;
}

function isRefreshRequired(json: unknown): boolean {
  const parsed = apiErrorSchema.safeParse(json);
  return parsed.success && parsed.data.errors.some((error) => error.code === 'REFRESH_REQUIRED');
}

function redirectToLogin() {
  if (window.location.pathname !== '/login') window.location.assign('/login');
}

// Token refresh state untuk mencegah multiple concurrent refresh calls
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Attempt to refresh access token via POST /auth/refresh.
 * Returns true if refresh successful, false otherwise.
 */
async function attemptRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const res = await fetch(buildUrl(ApiPath.AUTH.REFRESH), {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function buildUrl(path: string, params?: QueryParams) {
  if (!params) return `${API_BASE}${path}`;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v !== undefined && v !== null) search.append(key, String(v));
      }
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `${API_BASE}${path}?${qs}` : `${API_BASE}${path}`;
}

/**
 * Baca body sebagai teks lalu parse JSON secara aman.
 * Mengembalikan `undefined` bila body kosong / bukan JSON valid
 * (mis. respons 500 kosong dari Vite proxy saat backend mati),
 * sehingga caller bisa melempar error bermakna, bukan
 * "Unexpected end of JSON input".
 */
async function safeParseJson(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    if (!text || text.trim() === '') return undefined;
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function apiSuccessWithData<T extends ZodTypeAny>(dataSchema: T) {
  return z.object({
    success: z.literal(true),
    message: z.string(),
    data: dataSchema,
  });
}

async function request<T extends ZodTypeAny>(
  method: string,
  path: string,
  responseSchema: T,
  body?: unknown,
  params?: QueryParams,
  isRetry = false,
): Promise<z.infer<T>> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {},
  };
  if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(buildUrl(path, params), init);
  const json = await safeParseJson(res);

  if (!res.ok) {
    // Auto-refresh only for protected requests; retry each request once.
    if (res.status === 401 && !isRetry && shouldRefresh(method, path) && isRefreshRequired(json)) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        // Retry original request setelah refresh berhasil
        return request(method, path, responseSchema, body, params, true);
      } else {
        // Refresh gagal → redirect ke login
        redirectToLogin();
        throw new ApiClientError('Sesi berakhir. Silakan login kembali.', 401, 'UNAUTHORIZED');
      }
    }

    if (json === undefined) {
      throw new ApiClientError(
        `Server tidak merespons dengan benar (HTTP ${res.status})`,
        res.status,
        'INVALID_RESPONSE',
      );
    }
    const parsed = apiErrorSchema.safeParse(json);
    const message = parsed.success ? parsed.data.message : `Request gagal (${res.status})`;
    const code = parsed.success ? (parsed.data.errors[0]?.code ?? 'UNKNOWN') : 'UNKNOWN';
    throw new ApiClientError(message, res.status, code);
  }

  const parsed = apiSuccessWithData(responseSchema).parse(json);
  return parsed.data;
}

export async function apiGet<T extends ZodTypeAny>(
  path: string,
  responseSchema: T,
  params?: QueryParams,
): Promise<z.infer<T>> {
  return request('GET', path, responseSchema, undefined, params);
}

export async function apiPost<T extends ZodTypeAny>(
  path: string,
  body: unknown,
  responseSchema: T,
): Promise<z.infer<T>> {
  return request('POST', path, responseSchema, body);
}

export async function apiPatch<T extends ZodTypeAny>(
  path: string,
  body: unknown,
  responseSchema: T,
): Promise<z.infer<T>> {
  return request('PATCH', path, responseSchema, body);
}

export async function apiPut<T extends ZodTypeAny>(
  path: string,
  body: unknown,
  responseSchema: T,
): Promise<z.infer<T>> {
  return request('PUT', path, responseSchema, body);
}

/**
 * Upload multipart/form-data (mis. file logo) tanpa header Content-Type
 * manual — boundary diatur otomatis oleh browser/runtime.
 */
export async function apiUpload<T extends ZodTypeAny>(
  method: 'POST' | 'PUT',
  path: string,
  formData: FormData,
  responseSchema: T,
  isRetry = false,
): Promise<z.infer<T>> {
  const res = await fetch(buildUrl(path), {
    method,
    credentials: 'include',
    body: formData,
  });
  const json = await safeParseJson(res);

  if (!res.ok) {
    // Auto-refresh only for protected requests; retry each request once.
    if (res.status === 401 && !isRetry && shouldRefresh(method, path) && isRefreshRequired(json)) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return apiUpload(method, path, formData, responseSchema, true);
      } else {
        redirectToLogin();
        throw new ApiClientError('Sesi berakhir. Silakan login kembali.', 401, 'UNAUTHORIZED');
      }
    }

    if (json === undefined) {
      throw new ApiClientError(
        `Server tidak merespons dengan benar (HTTP ${res.status})`,
        res.status,
        'INVALID_RESPONSE',
      );
    }
    const parsed = apiErrorSchema.safeParse(json);
    const message = parsed.success ? parsed.data.message : `Request gagal (${res.status})`;
    const code = parsed.success ? (parsed.data.errors[0]?.code ?? 'UNKNOWN') : 'UNKNOWN';
    throw new ApiClientError(message, res.status, code);
  }

  const parsed = apiSuccessWithData(responseSchema).parse(json);
  return parsed.data;
}

export async function apiDelete(path: string, isRetry = false): Promise<void> {
  const res = await fetch(buildUrl(path), {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const json = await safeParseJson(res);
    // Auto-refresh only for protected requests; retry each request once.
    if (
      res.status === 401 &&
      !isRetry &&
      shouldRefresh('DELETE', path) &&
      isRefreshRequired(json)
    ) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return apiDelete(path, true);
      } else {
        redirectToLogin();
        throw new ApiClientError('Sesi berakhir. Silakan login kembali.', 401, 'UNAUTHORIZED');
      }
    }

    const parsed = apiErrorSchema.safeParse(json);
    const message = parsed.success ? parsed.data.message : `Request gagal (${res.status})`;
    const code = parsed.success ? (parsed.data.errors[0]?.code ?? 'UNKNOWN') : 'UNKNOWN';
    throw new ApiClientError(message, res.status, code);
  }
}

const pageMetaSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  total_pages: z.number(),
});

const apiListSchema = <T extends ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    message: z.string(),
    data: z.array(dataSchema),
    meta: pageMetaSchema,
  });

export async function apiList<T extends ZodTypeAny>(
  path: string,
  itemSchema: T,
  params?: QueryParams,
  isRetry = false,
): Promise<{ rows: z.infer<T>[]; meta: z.infer<typeof pageMetaSchema> }> {
  const res = await fetch(buildUrl(path, params), {
    method: 'GET',
    credentials: 'include',
  });
  const json = await safeParseJson(res);

  if (!res.ok) {
    // Auto-refresh only for protected requests; retry each request once.
    if (res.status === 401 && !isRetry && shouldRefresh('GET', path) && isRefreshRequired(json)) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return apiList(path, itemSchema, params, true);
      } else {
        redirectToLogin();
        throw new ApiClientError('Sesi berakhir. Silakan login kembali.', 401, 'UNAUTHORIZED');
      }
    }

    if (json === undefined) {
      throw new ApiClientError(
        `Server tidak merespons dengan benar (HTTP ${res.status})`,
        res.status,
        'INVALID_RESPONSE',
      );
    }
    const parsed = apiErrorSchema.safeParse(json);
    const message = parsed.success ? parsed.data.message : `Request gagal (${res.status})`;
    const code = parsed.success ? (parsed.data.errors[0]?.code ?? 'UNKNOWN') : 'UNKNOWN';
    throw new ApiClientError(message, res.status, code);
  }

  const parsed = apiListSchema(itemSchema).parse(json);
  return { rows: parsed.data, meta: parsed.meta };
}

export { ApiPath };
